import { useMemo, useState } from 'react';
import { Button, Input, Switch, useDisclosure, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@heroui/react';
import type { ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/common/PageHeader';
import { Pagination } from '@/components/common/Pagination';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable, type ColumnMetaExt } from '@/components/common/DataTable';
import { useTableQueryState } from '@/hooks/useTableQueryState';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useForwardersList, useUpdateForwarder, useDeleteForwarder } from '@/features/forwarders/hooks';
import { ForwarderFormModal } from '@/features/forwarders/components/ForwarderFormModal';
import type { Forwarder } from '@/features/forwarders/types';
import { PlusIcon, MagnifyingGlassIcon, PencilSquareIcon, TrashIcon, EllipsisVerticalIcon } from '@/icons';
import { useOpenOnNewParam } from '@/hooks/useOpenOnNewParam';
import { usePermission } from '@/hooks/usePermission';

export function ForwardersPage() {
  const { state, setPage, setPageSize, setSearch, toggleSort } = useTableQueryState();
  const [term, setTerm] = useState(state.q);
  const debounced = useDebouncedValue(term, 300);
  useMemo(() => { if (debounced !== state.q) setSearch(debounced); }, [debounced]); // eslint-disable-line

  const { data, isLoading, isError, error, refetch } = useForwardersList({ page: state.page, pageSize: state.pageSize, q: state.q || undefined, sort: state.sort || undefined });
  const form = useDisclosure();
  const [target, setTarget] = useState<Forwarder | null>(null);
  // Create/edit/delete need mailbox:manage at the gateway.
  const canManage = usePermission().can('mailbox:manage');
  useOpenOnNewParam(() => { if (canManage) { setTarget(null); form.onOpen(); } });
  const [toDelete, setToDelete] = useState<Forwarder | null>(null);
  const update = useUpdateForwarder();
  const del = useDeleteForwarder();

  const columns = useMemo<ColumnDef<Forwarder, unknown>[]>(() => [
    { id: 'address', header: 'Source', meta: { sortKey: 'address' } as ColumnMetaExt, cell: ({ row }) => <span className="font-medium text-fg">{row.original.address}</span> },
    { id: 'destinations', header: 'Forwards to', cell: ({ row }) => (
      <div className="flex flex-wrap gap-1">
        {row.original.destinations.slice(0, 3).map((d, i) => <span key={i} className="rounded-md bg-surface-raised px-2 py-0.5 text-xs text-fg-muted">{d}</span>)}
        {row.original.destinations.length > 3 && <span className="text-xs text-fg-subtle">+{row.original.destinations.length - 3}</span>}
      </div>
    ) },
    { id: 'keepCopy', header: 'Keep copy', cell: ({ row }) => <span className="text-sm text-fg-muted">{row.original.keepCopy ? 'Yes' : 'No'}</span> },
    { id: 'enabled', header: 'Enabled', cell: ({ row }) => (
      <Switch size="sm" isSelected={row.original.enabled} aria-label="Toggle forwarder" onValueChange={(v) => update.mutate({ id: row.original.id, body: { enabled: v } })} />
    ) },
    { id: 'actions', header: '', meta: { className: 'text-right w-12' } as ColumnMetaExt, cell: ({ row }) => (
      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
        <Dropdown placement="bottom-end">
          <DropdownTrigger><Button isIconOnly size="sm" variant="light" aria-label="Forwarder actions"><EllipsisVerticalIcon className="h-5 w-5 text-fg-muted" /></Button></DropdownTrigger>
          <DropdownMenu aria-label="Forwarder actions">
            {[<DropdownItem key="edit" startContent={<PencilSquareIcon className="h-4 w-4" />} onPress={() => { setTarget(row.original); form.onOpen(); }}>Edit</DropdownItem>,
            ...(canManage ? [<DropdownItem key="delete" className="text-danger" color="danger" startContent={<TrashIcon className="h-4 w-4" />} onPress={() => setToDelete(row.original)}>Delete</DropdownItem>] : [])]}
          </DropdownMenu>
        </Dropdown>
      </div>
    ) },
  ], [update, form, canManage]);

  return (
    <div>
      <PageHeader title="Forwarders" description="Fan mail out to one or more destinations."
        actions={canManage ? <Button color="primary" startContent={<PlusIcon className="h-4 w-4" />} onPress={() => { setTarget(null); form.onOpen(); }}>Create forwarder</Button> : undefined} />
      <div className="rounded-xl border border-border bg-surface">
        <div className="border-b border-border p-4">
          <Input value={term} onValueChange={setTerm} placeholder="Search forwarders…" startContent={<MagnifyingGlassIcon className="h-4 w-4 text-fg-subtle" />}
            variant="bordered" size="sm" className="sm:max-w-xs" aria-label="Search forwarders" isClearable onClear={() => { setTerm(''); setSearch(''); }} />
        </div>
        <DataTable<Forwarder> columns={columns} data={data?.data ?? []} getRowId={(f) => f.id} isLoading={isLoading} isError={isError} error={error} onRetry={() => refetch()} sort={state.sort} onSort={toggleSort} emptyMessage="No forwarders yet." />
        <Pagination page={state.page} pageSize={state.pageSize} total={data?.total} onPageChange={setPage} onPageSizeChange={setPageSize} />
      </div>
      <ForwarderFormModal isOpen={form.isOpen} onClose={form.onClose} forwarder={target} />
      <ConfirmDialog isOpen={!!toDelete} onClose={() => setToDelete(null)} onConfirm={async () => { if (toDelete) await del.mutateAsync(toDelete.id); setToDelete(null); }}
        title="Delete forwarder?" body={`Delete the forwarder ${toDelete?.address}?`} confirmLabel="Delete" tone="danger" isLoading={del.isPending} />
    </div>
  );
}
